import { createSlice } from "@reduxjs/toolkit";

import { Location } from "../../@types/location";
import { Organization } from "../../@types/organization"
import { UserProfileLocation } from "../../@types/userProfileLocation";
import { findLocations } from "./actions/findLocations";
import { findProfileLocations } from "./actions/findProfileLocations";
import { assignLocations } from "./actions/assignLocations";
import { User } from "../../@types/user";
import { Profile } from "../../@types/profile";

interface UserProfileLocationState {
  user: User;
  profile: Profile;
  locations: Location[];
  findLocationStatus: "idle" | "pending" | "succeeded" | "failed";
  findProfileLocationsStatus: "idle" | "pending" | "succeeded" | "failed";
  assignLocationsStatus: "idle" | "pending" | "succeeded" | "failed";
  searchText: string;
  selectedSearchText: string;
  selectedLocations: Location[];
  assignedLocations: UserProfileLocation[];
  maxListAvailableIndex: number; //Para listar con IonInfiniteScroll
  createFilters: {
    organization: Organization;
    businessArea: Location;
  };
};

const initialState = {
  user: {} as User,
  profile: {} as Profile,
  locations: [],
  findLocationStatus: "idle",
  findProfileLocationsStatus: "idle",
  assignLocationsStatus: "idle",
  searchText: "",
  selectedSearchText: "",
  selectedLocations: [],
  assignedLocations: [],
  maxListAvailableIndex: 20,
  createFilters: {
    organization: {} as Organization,
    businessArea: {} as Location,
  },
} as UserProfileLocationState;


const userProfileLocationSlice = createSlice({
  name: "userProfileLocation",
  initialState,
  reducers: {
    setInitialData(state) {
      return initialState;
    },
    setUser(state, { payload }) {
      state.user = payload;
    },
    setProfile(state, { payload }) {
      state.profile = payload;
    },
    setSearchText(state, { payload }) { 
      state.searchText = payload;
      state.maxListAvailableIndex = 20; 
    },
    setSelectedSearchText(state, { payload }){
      state.selectedSearchText = payload;
    },
    setMaxListAvailableIndex(state, { payload }) {
      state.maxListAvailableIndex = payload;
    },
    setOrganization(state, { payload }) { 
      state.createFilters.organization = payload;
      state.maxListAvailableIndex = 20;
     },
    setBusinessArea(state, { payload }) { 
      state.createFilters.businessArea = payload;
      state.maxListAvailableIndex = 20;
     },
    clearCreateFilters(state) {
      state.createFilters = {
        organization: {} as Organization,
        businessArea: {} as Location,
      };
      state.maxListAvailableIndex = 20;
    },
    setPreselectLocation(state, { payload }) {
      state.selectedLocations = [...state.selectedLocations, payload];
    },
    setPreselectAll(state, { payload }) {
      state.selectedLocations = [...state.selectedLocations, ...payload];
    },
    setDeselectLocation(state, { payload }) {
      state.selectedLocations = state.selectedLocations.filter(({ id }) => id !== payload.id);
    },
    setDeselectAll(state) {
      state.selectedLocations = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(findLocations.pending, (state) => {
        state.findLocationStatus = "pending";
        state.locations = [];
      })
      .addCase(findLocations.fulfilled, (state, { payload }) => {
        state.findLocationStatus = "succeeded";
        state.locations = payload;
      })
      .addCase(findLocations.rejected, (state) => {
        state.findLocationStatus = "failed";
        state.locations = [];
      })
      .addCase(findProfileLocations.pending, (state) => {
        state.findProfileLocationsStatus = "pending";
        state.selectedLocations = [];
        state.assignedLocations = [];
      })
      .addCase(findProfileLocations.fulfilled, (state, { payload }) => {
        state.findProfileLocationsStatus = "succeeded";
        state.selectedLocations = payload.map((upl: { location: Location; }) => upl.location);
        state.assignedLocations = payload;
      })
      .addCase(findProfileLocations.rejected, (state) => {
        state.findProfileLocationsStatus = "failed";
        state.selectedLocations = [];
        state.assignedLocations = [];
      })
      .addCase(assignLocations.pending, (state) => {
        state.assignLocationsStatus = "pending";
      })
      .addCase(assignLocations.fulfilled, (state) => {
        state.assignLocationsStatus = "succeeded";
      })
      .addCase(assignLocations.rejected, (state) => {
        state.assignLocationsStatus = "failed";
      });
  },
});

export const {
  setInitialData,
  setUser,
  setProfile,
  setSearchText,
  setSelectedSearchText,
  setMaxListAvailableIndex,
  setOrganization,
  setBusinessArea,
  clearCreateFilters,
  setPreselectLocation,
  setPreselectAll,
  setDeselectLocation,
  setDeselectAll,
} = userProfileLocationSlice.actions

export default userProfileLocationSlice.reducer